import {
    createApi,
    fetchBaseQuery,
    type BaseQueryFn,
    type FetchArgs,
    type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'

import type { RootState } from '@/app/store'
import { deepSnakeToCamel } from '@/utils/caseTransform'
import {
    isMfaRequiredError,
    isOrgSuspendedError,
    logout,
    setMfaRequired,
    setOrgSuspended,
} from '@/modules/auth/authSlice'
import { showNotification } from '@/app/notificationSlice'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? '/api'

const rawBaseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
        const token = (getState() as RootState).auth.token
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        return headers
    },
})

/** Public sign-in endpoints: their 401s mean "wrong credentials", never "session expired". */
const SIGN_IN_PATHS = new Set([
    '/auth/login',
    '/auth/google',
    '/auth/mfa/verify',
    '/auth/mfa/recovery',
])

export function isSignInRequest(args: string | FetchArgs): boolean {
    const path = (typeof args === 'string' ? args : args.url).split('?')[0]
    return SIGN_IN_PATHS.has(path) || path.startsWith('/auth/invites/')
}

/**
 * Custom baseQuery wrapper that converts all snake_case keys in API responses
 * to camelCase. This is the single source of truth for response normalisation —
 * no per-endpoint mapping required.
 */
const camelCaseBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
    args,
    api,
    extraOptions,
) => {
    const result = await rawBaseQuery(args, api, extraOptions)

    // A 401 from a sign-in step (wrong password / code) is an answer, not an expired session.
    if (result.error && result.error.status === 401 && !isSignInRequest(args)) {
        api.dispatch(logout())
        api.dispatch(
            showNotification({
                message: 'Your session has expired. Please log in again.',
                severity: 'warning',
            }),
        )
        return result
    }

    if (result.error && isMfaRequiredError(result.error.status, result.error.data)) {
        api.dispatch(setMfaRequired(true))
    }
    if (result.error && isOrgSuspendedError(result.error.status, result.error.data)) {
        api.dispatch(setOrgSuspended(true))
    }

    if (result.data) {
        result.data = deepSnakeToCamel(result.data)
    }
    return result
}

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: camelCaseBaseQuery,
    tagTypes: [
        'Analytics',
        'Projects',
        'Dashboards',
        'ApiKeys',
        'Tenants',
        'Agents',
        'Billing',
        'Admin',
        'Lenses',
    ],
    endpoints: () => ({}),
})
